<template>
<div class="px-1 pt-1">
  <div class="input-group pb-3">
    <RouterLink :to="{ name: 'stock-changes' }" class="btn btn-outline-secondary">
      <font-awesome-icon :icon="['fas', 'chevron-left']" />
      <span class="d-none d-sm-inline-block ms-1">{{ $t('global.list') }}</span>
    </RouterLink>
    <RouterLink :to="{ name: 'item', params: { itemId: itemId } }" :title="$t('menu.items')" class="btn btn-outline-secondary">
      <font-awesome-icon :icon="['fas', 'rectangle-list']" fixed-width />
    </RouterLink>
    <RouterLink :to="{ name: 'stocks', params: { itemId: itemId }}" :title="$t('menu.stocks')" class="btn btn-outline-secondary">
      <font-awesome-icon :icon="['fas', 'boxes-stacked']" />
    </RouterLink>
    <span class="input-group-text">
      <ItemField :item="item" />
    </span>
    <button class="btn btn-outline-secondary" @click="refresh()" :disabled="isLoading">
      <div v-if="isLoading" class="spinner-border spinner-border-sm"></div>
      <font-awesome-icon v-else :icon="['fas', 'rotate']" />
      <span class="d-none d-sm-inline ms-1">{{ $t('action.refresh') }}</span>
    </button>
    <button
      v-if="userSettingsStore.data.developerMode"
      class="btn"
      :class="ignoreSignature ? 'btn-primary' : 'btn-outline-secondary'"
      :title="$t('action.ignoreSignature')"
      :aria-pressed="ignoreSignature"
      @click="ignoreSignature = !ignoreSignature"
      :disabled="isLoading">
      <font-awesome-icon :icon="['fas', 'layer-group']" />
    </button>
    <TypeaheadComponent
      v-if="!ignoreSignature && availableLots.length > 1"
      v-model="selectedLot"
      :items="availableLots"
      :item-projection="lotProjection"
      :placeholder="$t('field.masterdata.lot')"
      :disabled="isLoading"
      :min-input-length="0"
      :max-items="20"
      :input-class="'form-control'"
      @update:modelValue="onLotSelected"
    />
    <button class="btn btn-outline-secondary" @click="resetFilter()" :disabled="isLoading" v-if="!ignoreSignature && selectedLot">
      <font-awesome-icon :icon="['fas', 'filter-slash']" />
    </button>
  </div>

  <div v-if="error" class="alert alert-danger">
    {{ error }}
  </div>
  <div v-else-if="uiList" id="content-page-table" class="table-fix-head" :style="fixHeadCalculatedStyle">
    <table v-if="uiList.length > 0" class="table table-hover table-sm table-responsive-lg table-fit align-middle">
      <thead>
        <tr>
          <th v-if="selectedSiteId === 0">{{ $t('global.site') }}</th>
          <th colspan="4">{{ $t('field.generic.quantity') }}</th>
          <th v-if="!ignoreSignature">{{ $t('field.masterdata.lot') }}</th>
          <th v-if="!ignoreSignature && hasBestBeforeOnAnyLine">{{ $t('field.masterdata.bestBefore') }}</th>
          <th v-if="!ignoreSignature && hasExpiryOnAnyLine">{{ $t('field.masterdata.expiry') }}</th>
          <th class="d-none d-lg-table-cell">
            {{ $t('field.generic.created') }}
            <font-awesome-icon :icon="['fas', 'sort-down']" />
          </th>
          <th v-if="userSettingsStore.data.developerMode" class="d-none d-xl-table-cell"></th>
        </tr>
      </thead>
      <tbody id="scrollable-content">
        <tr v-for="stockChange in displayList" :key="stockChange.id">
          <td v-if="selectedSiteId === 0"><sup class="text-secondary">{{ stockChange.site.code }}&nbsp;</sup>{{ stockChange.site.name }}</td>
          <QuantityChangeCell :stockChange="stockChange" :pad-length="maxQuantityLength" />
          <td v-if="!ignoreSignature"><LotField :item="stockChange.item" :lot="stockChange.lot" /></td>
          <td class="text-center" v-if="!ignoreSignature && hasBestBeforeOnAnyLine"><BestBeforeField :stock="stockChange" /></td>
          <td class="text-center" v-if="!ignoreSignature && hasExpiryOnAnyLine"><ExpirationField :stock="stockChange" /></td>
          <AuditCreatedField :audit="stockChange.audit" />
          <td v-if="userSettingsStore.data.developerMode" class="d-none d-xl-table-cell text-end">
            <button
              class="btn btn-outline-danger btn-sm"
              :title="$t('action.undoStockChange')"
              :disabled="isLoading || undoingId === stockChange.id || ignoreSignature"
              @click="undoStockChange(stockChange)">
              <div v-if="undoingId === stockChange.id" class="spinner-border spinner-border-sm"></div>
              <font-awesome-icon v-else :icon="['fas', 'rotate-left']" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <span v-else>{{ $t('error.noData') }}</span>
  </div>
  <div v-else class="loading">
    {{ $t('status.loadingEllipsis') }}
  </div>
</div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { storeToRefs } from 'pinia';
import Semaphore from "@/services/semaphore";
import { useItemsStore, useSessionStore, useStockChangeStore, useUserSettingsStore } from '@/stores';
import QuantityChangeCell from "@/components/cells/QuantityChangeCell.vue";
import { AuditCreatedField, BestBeforeField, ExpirationField, LotField, ItemField } from "@/components/fields";
import IItemSignatureRaw from "@/models/IItemSignatureRaw";
import FrontendStockChange from "@/models/frontend/StockChange";
import TypeaheadComponent from "@/components/TypeaheadComponent.vue";

const { t } = useI18n();
const sessionStore = useSessionStore();
const userSettingsStore = useUserSettingsStore();
const itemsStore = useItemsStore();
const stockChangesStore = useStockChangeStore();

const { selectedSiteId } = storeToRefs(sessionStore);
const { data, error, moreItems } = storeToRefs(stockChangesStore);

const pageSize = 100;
const currentPage = ref(0);
const isLoading = ref(false);
const semaphore = new Semaphore(1);
const offsetTop = ref(0);
const availableLots = ref<IItemSignatureRaw[]>([]);
const selectedLot = ref<IItemSignatureRaw | null>(null);
const ignoreSignature = ref(false);
const undoingId = ref<number | null>(null);

const item = computed(() => {
  return itemsStore.getById(props.itemId);
});

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

const props = defineProps({
  itemId: { type: Number, required: true }
});

onMounted(async () => {
  currentPage.value = 0;
  await refresh();
  updateObserver();
  offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
});

const maxQuantityLength = computed(() => {
  return displayList.value.reduce((acc, item) => Math.max(acc, item.quantity.toString().length), 0);
});

// IntersectionObserver callback
function handleIntersection(entries: IntersectionObserverEntry[]) {
  entries.forEach(async entry => {
    if (entry.isIntersecting) {
      if (moreItems.value) {
        await loadNextPage();
      }
      else {
        observer.unobserve(entry.target);
      }
    }
  });
}

// Create the IntersectionObserver
const observer = new IntersectionObserver(handleIntersection, {
  root: null, // Use the viewport as the root
  threshold: 1.0, // Trigger when 100% of the target is visible
});

async function loadNextPage() {
  isLoading.value = true;
  const currentLength = data.value.length;
  await semaphore.runWithSemaphore(async () => {
    while (moreItems.value && data.value.length <= currentLength) {
      await addPage();
    }
  });
  isLoading.value = false;
  updateObserver();
}

// Update observer to observe the last row
function updateObserver() {
  const tableBody = document.getElementById('scrollable-content');
  if (!tableBody) {
    return;
  }

  const rows = tableBody.getElementsByTagName('tr');
  const index = Math.max(rows.length - 5, 0); // Observe the 5th last row or lesser if there are less rows
  observer.observe(rows[index]!);
}

const resetFilter = async () => {
  selectedLot.value = null;
}

async function undoStockChange(stockChange: FrontendStockChange) {
  if (undoingId.value !== null) return;
  if (!window.confirm(t('instruction.undoStockChangeConfirm'))) return;

  undoingId.value = stockChange.id;
  try {
    await stockChangesStore.undoStockChange(stockChange.id);
    await refresh();
  }
  finally {
    undoingId.value = null;
  }
}

// Function to project lot display text for typeahead
const lotProjection = (signature: IItemSignatureRaw | null): string => {
  if (!signature || !signature.lot) return "";
  return signature.lot;
}

// Function called when a lot is selected from the dropdown
const onLotSelected = (signature: IItemSignatureRaw | null) => {
  selectedLot.value = signature;
}

async function refresh() {
  isLoading.value = true;
  error.value = "";

  try {
    currentPage.value = 0;
    await stockChangesStore.fetchPageByItem(props.itemId, pageSize);

    const signatures = await stockChangesStore.fetchSignaturesForItem(props.itemId);
    // Get unique lots
    const uniqueLots = signatures
      .filter(sig => sig.lot) // Only signatures with lots
      .reduce((acc: IItemSignatureRaw[], current) => {
        if (!acc.find(sig => sig.lot === current.lot)) {
          acc.push(current);
        }
        return acc;
      }, [])
      .sort((a, b) => (a.lot || "").localeCompare(b.lot || ""));

    availableLots.value = uniqueLots;
  }
  finally {
    isLoading.value = false;
  }
}
async function addPage() {
  isLoading.value = true;
  error.value = "";

  try {
    currentPage.value++;
    await stockChangesStore.fetchPageAdditiveByItem(props.itemId, pageSize);
  }
  finally {
    isLoading.value = false;
  }
}

const hasBestBeforeOnAnyLine = computed(() => {
  return data.value !== undefined && data.value.findIndex((x) => !!x.bestBefore) !== -1;
});

const hasExpiryOnAnyLine = computed(() => {
  return data.value !== undefined && data.value.findIndex((x) => !!x.expiryAsDate) !== -1;
});

const uiList = computed(() => {
  return data.value.filter((item) => {
    return !selectedLot.value || (item.lot && item.lot.toLowerCase().indexOf((selectedLot.value.lot ?? "").toLowerCase()) != -1);
  });
});

const displayList = computed<FrontendStockChange[]>(() => {
  if (!ignoreSignature.value) {
    return uiList.value;
  }

  // When ignoring signature, aggregate across all signatures of this item:
  // sum the latest known balance per (lot, bestBefore, expiry) signature to get
  // the current item-level total, then walk newest-first subtracting each row's
  // relative change to reconstruct the post-change item total for older rows.
  const sortedAsc = [...data.value].sort((a, b) => a.id - b.id);
  const latestBySignature = new Map<string, FrontendStockChange>();
  for (const change of sortedAsc) {
    const key = `${change.lot ?? ''}|${change.bestBefore ?? ''}|${change.expiry ?? ''}`;
    latestBySignature.set(key, change);
  }
  let postTotal = 0;
  for (const change of latestBySignature.values()) {
    postTotal += change.quantity;
  }

  return data.value.map((change) => {
    const total = postTotal;
    postTotal -= change.realativeQuantity;
    return { ...change, quantity: total } as FrontendStockChange;
  });
});

watch([selectedLot], () => {
  if (moreItems.value) {
    nextTick(async () => {
      loadNextPage();
    });
  }
});
</script>
